import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";

export default function RegisterAdmin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", institution: "", password: "", confirm_password: "", admin_code: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register/admin", form);
      login(data.token, data.user);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Administrator registration"
      subtitle="Authorized personnel only. A valid Admin Registration Code is required."
      side="Run your institution with confidence."
      image="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2000"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="admin-register-form">
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 px-3 py-2" data-testid="admin-register-error">
            {error}
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Full Name</Label>
          <Input value={form.name} onChange={set("name")} required data-testid="admin-name" />
        </div>
        <div className="space-y-1.5">
          <Label>Email Address</Label>
          <Input type="email" value={form.email} onChange={set("email")} required data-testid="admin-email" />
        </div>
        <div className="space-y-1.5">
          <Label>Institution Name</Label>
          <Input value={form.institution} onChange={set("institution")} required data-testid="admin-institution" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input type="password" value={form.password} onChange={set("password")} required data-testid="admin-password" />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm</Label>
            <Input type="password" value={form.confirm_password} onChange={set("confirm_password")} required data-testid="admin-confirm" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Secret Admin Registration Code
          </Label>
          <Input value={form.admin_code} onChange={set("admin_code")} required data-testid="admin-code" placeholder="Provided by platform owner" className="font-mono" />
        </div>
        <Button type="submit" className="w-full rounded-sm" disabled={loading} data-testid="admin-register-submit">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create admin account
        </Button>
      </form>
      <div className="mt-6 text-sm text-muted-foreground space-y-1">
        <div>
          Already have an admin account?{" "}
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="admin-link-login">Sign in here</Link>
        </div>
        <div>
          Not an admin?{" "}
          <Link to="/register" className="text-primary font-medium hover:underline">Student registration</Link>
        </div>
      </div>
    </AuthShell>
  );
}
